import type { Tool } from '../../types'
import { newTool } from '../../utils/tools'

/** Ref callback that auto-sizes a textarea to its content on mount and re-render. */
function autoSizeRef(el: HTMLTextAreaElement | null) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

const MILLING_TOOL_TYPES = ['Endmill', 'Drill', 'Face Mill', 'Ball Mill', 'Chamfer', 'Tap', 'Reamer']
const TURNING_TOOL_TYPES = ['OD Insert', 'ID Boring Bar', 'Parting', 'Threading', 'Face Insert']

interface ToolLibraryEditorProps {
  tools: Tool[]
  operationType?: 'milling' | 'turning'
  onChange: (tools: Tool[]) => void
}

export default function ToolLibraryEditor({ tools, operationType = 'milling', onChange }: ToolLibraryEditorProps) {
  const toolTypes = operationType === 'turning' ? TURNING_TOOL_TYPES : MILLING_TOOL_TYPES

  const addTool = () => {
    const nextNumber = tools.length > 0 ? Math.max(...tools.map(t => t.number)) + 1 : 1
    onChange([...tools, newTool(nextNumber, operationType)])
  }

  const removeTool = (id: string) => {
    onChange(tools.filter(t => t.id !== id))
  }

  const updateTool = (id: string, patch: Partial<Tool>) => {
    onChange(tools.map(t => t.id === id ? { ...t, ...patch } : t))
  }

  const inputClass = `
    bg-white/5 border border-cyan-500/20 rounded
    font-mono text-xs text-slate-200 px-2 py-1.5
    focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30
    transition-all duration-150
  `

  return (
    <div className="space-y-3">
      {/* Tool rows */}
      <div className="space-y-2">
        {tools.length === 0 ? (
          <p className="font-mono text-xs text-slate-600 text-center py-3">
            No tools yet — click + Add Tool
          </p>
        ) : (
          tools.map(tool => (
            <div key={tool.id} className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Tool number */}
                <span className="font-mono text-xs text-cyan-500 w-8 shrink-0">
                  T{String(tool.number).padStart(2, '0')}
                </span>

                {/* Type */}
                <div className="relative shrink-0">
                  <select
                    value={tool.type}
                    onChange={e => updateTool(tool.id, { type: e.target.value })}
                    className={`${inputClass} appearance-none pr-5`}
                  >
                    {toolTypes.map(t => (
                      <option key={t} value={t} className="bg-[#0d1526]">{t}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-cyan-500/60 text-xs">
                    ▾
                  </div>
                </div>

                {/* Diameter */}
                <div className="flex items-center gap-1 shrink-0">
                  <span className="font-mono text-xs text-slate-500">⌀</span>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={tool.diameter || ''}
                    onChange={e => updateTool(tool.id, { diameter: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    className={`${inputClass} w-16`}
                  />
                  <span className="font-mono text-xs text-slate-500">mm</span>
                </div>

                {/* Stickout */}
                <div className="flex items-center gap-1 shrink-0">
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={tool.stickout || ''}
                    onChange={e => updateTool(tool.id, { stickout: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    className={`${inputClass} w-16`}
                  />
                  <span className="font-mono text-xs text-slate-500">mm out</span>
                </div>

                {/* Flutes */}
                <div className="flex items-center gap-1 shrink-0">
                  <input
                    type="number"
                    min={0}
                    max={12}
                    value={tool.flutes || ''}
                    onChange={e => updateTool(tool.id, { flutes: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className={`${inputClass} w-10`}
                  />
                  <span className="font-mono text-xs text-slate-500">fl</span>
                </div>

                {/* Can Plunge */}
                <label className="flex items-center gap-1.5 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tool.canPlunge}
                    onChange={e => updateTool(tool.id, { canPlunge: e.target.checked })}
                    className="accent-cyan-500 w-3 h-3"
                  />
                  <span className="font-mono text-xs text-slate-500">plunge</span>
                </label>

                {/* Remove */}
                <button
                  type="button"
                  onClick={() => removeTool(tool.id)}
                  className="shrink-0 font-mono text-xs text-slate-600 hover:text-red-400 transition-colors px-1"
                  aria-label="Remove tool"
                >
                  ✕
                </button>
              </div>

              {/* Notes — full-width auto-sizing textarea */}
              <div className="pl-10">
                <textarea
                  ref={autoSizeRef}
                  key={tool.description}
                  defaultValue={tool.description}
                  onBlur={e => updateTool(tool.id, { description: e.target.value })}
                  onInput={e => {
                    const t = e.target as HTMLTextAreaElement
                    t.style.height = 'auto'
                    t.style.height = t.scrollHeight + 'px'
                  }}
                  placeholder="Tool notes — tolerances, usage, special instructions…"
                  rows={1}
                  className={`${inputClass} w-full text-sm resize-none overflow-hidden leading-relaxed`}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add tool */}
      <button
        type="button"
        onClick={addTool}
        className="
          font-mono text-xs text-cyan-400 border border-cyan-500/30 rounded px-3 py-1.5
          hover:border-cyan-400 hover:bg-cyan-500/10 transition-all duration-150
        "
      >
        + Add Tool
      </button>
    </div>
  )
}
